// 显示格式：时间、百分比、文件大小、优先级与成熟度文字
import { formatDuration, relativeTime, sgDateTime, type Maturity, type Priority, type Role, type TaskTiming } from "@csp/shared";

/** 时间戳（业主定）：今天只显示时间；7 天内「N天前」；超 7 天「N周前」；超一个月「N个月前」 */
export function fmtTime(iso: string | null | undefined): string {
  return relativeTime(iso) || "—";
}

/** 完整时间（悬停提示 / 详情）：新加坡时间 YYYY-MM-DD HH:mm */
export function fmtDateTime(iso: string | null | undefined): string {
  return sgDateTime(iso) || "—";
}

export const fmtDur = formatDuration;

/** 列表里的一行耗时：排队中「已等 3分」；执行中「安排后 2分开始 · 已跑 5分（等人 1分）」；结束「等待 2分 · 用时 5分（等人 1分）」 */
export function timingText(t: TaskTiming, status: string): string {
  if (!t.queuedAt) return "";
  if (!t.startedAt) return status === "queued" ? `已等 ${formatDuration(t.waitMs)}` : "";
  const human = t.humanMs ? `（等人 ${formatDuration(t.humanMs)}）` : "";
  if (t.inProgress) return `安排后 ${formatDuration(t.waitMs)} 开始 · 已跑 ${formatDuration(t.runMs)}${human}`;
  return `等待 ${formatDuration(t.waitMs)} · 用时 ${formatDuration(t.runMs)}${human}`;
}

/** 表格里的耗时短写：排队中「已等 3分」；开始后「等待 2分 · 执行 5分 · 等人 1分」（等人为 0 时省略） */
export function timingShort(t: TaskTiming, status: string): string {
  if (!t.queuedAt) return "";
  if (!t.startedAt) return status === "queued" ? `已等 ${formatDuration(t.waitMs)}` : "";
  const parts = [`等待 ${formatDuration(t.waitMs)}`, `执行 ${formatDuration(t.runMs)}`];
  if (t.humanMs) parts.push(`等人 ${formatDuration(t.humanMs)}`);
  return parts.join(" · ");
}

export function fmtPct(v: number | null | undefined): string {
  return v === null || v === undefined ? "—" : `${Math.round(v * 100)}%`;
}

export function fmtSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export const PRIORITY_TEXT: Record<Priority, string> = { urgent: "紧急", high: "高", normal: "普通", low: "低" };
/** 删除任务确认文案（详情页、全部任务列表共用） */
export const REMOVE_HINT = "正在执行的会先中止、浏览器标签页会关闭；任务从列表移除，材料和录像保留，在「已归档」筛选里可以找回。";

export const ROLE_TEXT: Record<Role, string> = { clerk: "文员", admin: "管理员", boss: "老板" };

export const MATURITY_TEXT: Record<Maturity, string> = {
  L0: "L0 草稿",
  L1: "L1 跑通",
  L2: "L2 试用",
  L3: "L3 成熟",
};

export const REVIEWER_TEXT: Record<string, string> = { clerk: "文员", boss: "老板", either: "文员或老板", both: "文员和老板都要批" };

/** 字段 key → 中文名：manifest 字段 label，外加公司名 / UEN */
export function fieldLabel(key: string, labels: Record<string, string>): string {
  if (key === "companyName") return "公司名称";
  if (key === "uen") return "UEN";
  return labels[key] ?? key;
}
